/**
 * Guest Tokens API
 * Client-side API for the `guestTokenApi` callable: delegating a work order or
 * project to an external user, undelegating, and listing delegates with the
 * state of their personal link. The name is historical; access is granted by
 * the resource's `delegations/{uid}`, not by a per-task token.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
    CreateGuestTokenPayload,
    CreateGuestTokenResponse,
    Delegate,
    GuestTaskType,
    RevokeDelegationPayload,
} from '../types/guestAccess';
import { recordActivity } from './activityApi';

type GuestTokenAction = 'create' | 'revoke' | 'list';

interface ApiRequest {
    action: GuestTokenAction;
    [key: string]: unknown;
}

interface ListResponse {
    success: true;
    delegates: Delegate[];
}

interface SuccessResponse {
    success: true;
}

/**
 * API for delegations to external users (admin only)
 */
class GuestTokensApi {
    private functions = getFunctions(undefined, 'europe-north1');

    private async call<R>(request: ApiRequest): Promise<R> {
        const guestTokenApi = httpsCallable<ApiRequest, R>(
            this.functions,
            'guestTokenApi'
        );
        const result = await guestTokenApi(request);
        return result.data;
    }

    /**
     * Delegate a task to an external user, ensure they have a live personal
     * link (minted with `linkKind`/`expiresAt` when none) and optionally e-mail
     * the deep link to the task.
     */
    async create(payload: CreateGuestTokenPayload): Promise<CreateGuestTokenResponse> {
        const response = await this.call<CreateGuestTokenResponse>({
            action: 'create',
            ...payload,
        });
        recordActivity(payload.taskType === 'project' ? 'project.updated' : 'workOrder.updated', {
            buildingKey: payload.buildingKey,
            targetKey: payload.taskKey,
        });
        return response;
    }

    /**
     * Undelegate: removes the delegation and its mirror entry. The user's
     * personal link is untouched.
     */
    async revoke(payload: RevokeDelegationPayload): Promise<void> {
        await this.call<SuccessResponse>({
            action: 'revoke',
            ...payload,
        });
        recordActivity(payload.taskType === 'project' ? 'project.updated' : 'workOrder.updated', {
            targetKey: payload.taskKey,
        });
    }

    /**
     * Every external delegate of a task, each with their link's state.
     */
    async list(taskType: GuestTaskType, taskKey: string): Promise<Delegate[]> {
        const response = await this.call<ListResponse>({
            action: 'list',
            taskType,
            taskKey,
        });
        return response.delegates;
    }
}

export const guestTokensApi = new GuestTokensApi();
